// src/components/ThemeSwatches.jsx
// Teal · Ocean · Plum as three round swatches (3.47.0), for the Theme line of
// your menu. Each shows the theme's own two colours; the chosen one has a
// ring and a ✓. 3.47.2: `compact` = the dots only, side by side in an oval
// (like the light · auto · dark switch next to them), with the theme's name
// in a small tip on hover or keyboard focus. Same choice as Preferences →
// Appearance (components/ThemePicker): it saves to the account straight away.
import { useAuth } from '../hooks/useAuth';
import { usePreferences } from '../hooks/usePreferences';
import { THEMES, effectiveTheme } from '../theme/themes';

export default function ThemeSwatches({ id = 'theme', compact = false }) {
  const { userInfo } = useAuth();
  const { prefs, setPreference } = usePreferences();
  const value = effectiveTheme(prefs.theme, userInfo?.clientTheme);
  const index = THEMES.findIndex(t => t.id === value);

  function onKey(e) {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = THEMES[(index + step + THEMES.length) % THEMES.length];
    setPreference('theme', next.id);
    document.getElementById(`${id}-${next.id}`)?.focus();
  }

  return (
    <div role="radiogroup" aria-label="Colour theme" className={'cp-swatches' + (compact ? ' compact' : '')} onKeyDown={onKey}>
      <style>{CSS}</style>
      {THEMES.map(t => {
        const on = value === t.id, [a, , c] = t.preview.light.btn;
        return (
          <button key={t.id} id={`${id}-${t.id}`} type="button" role="radio" aria-checked={on} tabIndex={on ? 0 : -1}
            aria-label={`${t.label}: ${t.blurb}`}
            className={'cp-swatch' + (on ? ' on' : '')} onClick={() => setPreference('theme', t.id)}
            style={{ '--a': a, '--b': c }}>
            <span className="dot" aria-hidden="true">{on && <span className="tick">✓</span>}</span>
            {compact
              ? <span className="tip" aria-hidden="true">{t.label}</span>
              : <span className="name">{t.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

const CSS = `
.cp-swatches { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.cp-swatch { position: relative; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 5px 6px; border-radius: 10px; cursor: pointer; font: inherit; min-width: 0;
  background: rgb(var(--glass-rgb) / 0.6); border: 1.5px solid var(--c-cfd8dc); color: var(--t-37474f);
  transition: transform .2s cubic-bezier(.34,1.5,.64,1), box-shadow .2s ease, border-color .2s ease; }
/* 3.49.19: grows in place (moving up flickered at the edge) */
.cp-swatch:hover { transform: scale(1.04); box-shadow: 0 6px 14px rgb(var(--c-000000-rgb) / 0.12); }
.cp-swatch:focus-visible { outline: 2px solid var(--c-00897b); outline-offset: 2px; }
.cp-swatch.on { border-color: var(--a); box-shadow: 0 0 0 1.5px var(--a), 0 6px 16px rgb(var(--c-000000-rgb) / 0.14); }
.cp-swatch .dot { position: relative; flex-shrink: 0; width: 20px; height: 20px; border-radius: 50%; display: grid; place-items: center; overflow: hidden;
  background: linear-gradient(135deg, var(--a) 50%, var(--b) 50%); box-shadow: inset 0 0 0 2px rgb(var(--c-ffffff-rgb) / 0.35);
  transition: transform .35s cubic-bezier(.3,1.6,.5,1); }
.cp-swatch:hover .dot { transform: rotate(-20deg) scale(1.08); }
.cp-swatch .dot::after { content: ''; position: absolute; top: 0; bottom: 0; width: 45%; left: -60%;
  background: linear-gradient(100deg, transparent, rgb(var(--c-ffffff-rgb) / 0.55), transparent); transform: skewX(-18deg); }
.cp-swatch.on .dot::after, .cp-swatch:hover .dot::after { animation: cpSwatchShine 2.4s ease-in-out infinite; }
.cp-swatch .tick { position: relative; z-index: 1; color: #fff; font-size: 11px; font-weight: 800; text-shadow: 0 1px 2px rgb(0 0 0 / 0.35); animation: cpSwatchPop .35s cubic-bezier(.3,1.8,.5,1); }
.cp-swatch .name { font-size: 12.5px; font-weight: 700; white-space: nowrap; }
.cp-swatch.on .name { color: var(--t-00695c); }
/* compact: the dots only, in an oval like the light · auto · dark switch */
.cp-swatches.compact { display: inline-flex; gap: 2px; padding: 3px; border-radius: 999px; background: var(--c-eceff1); border: 1.5px solid var(--c-cfd8dc); }
.cp-swatches.compact .cp-swatch { width: 30px; height: 26px; padding: 0; border: none; border-radius: 999px; background: none; box-shadow: none; }
.cp-swatches.compact .cp-swatch:hover { transform: none; background: rgb(var(--c-ffffff-rgb) / 0.5); }
.cp-swatches.compact .cp-swatch.on .dot { box-shadow: 0 0 0 2px var(--c-ffffff), 0 0 0 3.5px var(--a); }
.cp-swatch .tip { position: absolute; top: calc(100% + 7px); left: 50%; transform: translateX(-50%) translateY(-4px); opacity: 0; pointer-events: none;
  white-space: nowrap; font-size: 11.5px; font-weight: 700; padding: 3px 8px; border-radius: 6px; z-index: 5;
  background: var(--c-263238); color: var(--t-ffffff); box-shadow: 0 4px 12px rgb(var(--c-000000-rgb) / 0.2); transition: opacity .15s ease, transform .15s ease; }
.cp-swatch:hover .tip, .cp-swatch:focus-visible .tip { opacity: 1; transform: translateX(-50%); }
@keyframes cpSwatchShine { 0% { left: -60%; } 60%, 100% { left: 130%; } }
@keyframes cpSwatchPop { from { transform: scale(0); } to { transform: none; } }
`;
